import React, { useCallback, memo, ReactElement, ChangeEvent } from "react";
import cn from "classnames";
import { useFormikContext } from "formik";
import { UploadField } from "../../UploadField/UploadField";
import { getScreenByStep, impressionBase } from "../constants";
import styles from "./Documents.module.scss";

/**
 * Converts a Blob file to a Base64 string wile removing the data type.
 *
 * @returns {Promise} - A Promise that resolves with the Base64 string or rejects with an error.
 */
const convertToBase64 = async (file: Blob): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = () => {
            const dataURL = fileReader.result as string;
            resolve(dataURL);
        };

        fileReader.onerror = (error) => {
            reject(error);
        };

        fileReader.readAsDataURL(file);
    });
};

/**
 * Renders a confirmation message to provide visual feedback to the user that their upload was successful and to
 * inform them of the next steps in the process. It takes in various props related to the resume and cover letter,
 * and uses the UploadField component to handle file uploads.
 *
 * @returns {ReactElement} The Document screen.
 */
const Documents = memo((props: {
    enabled: boolean;
}): ReactElement => {
    const {
        enabled = false,
    } = props;
    const { errors, touched, setTouched, setFieldValue, setFieldError, values } = useFormikContext();
    const errorObj = (errors as Record<string, string>);
    const touchedObj = (touched as Record<string, string>);
    const valuesObj = (values as Record<string, string>);
    const screen = getScreenByStep(3);
    const impression = {
        ...impressionBase,
        sp_modal_screen_name: screen.name,
        sp_modal_screen_order: screen.step,
    };

    /**
     * Checks for a valid file then converts it to base64 to store the string.
     */
    const onChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
        const fieldName = event.target?.name ?? "";
        const file = event.target?.files?.[0] ?? undefined;
        if (!fieldName) return;

        // Goldilocks says This file is too small.
        if (!file) {
            setFieldValue(fieldName, "", true);
            setTouched(fieldName, false);
            return;
        }

        // Goldilocks says This file is too big.
        if (file.size / 1048576 > 4.9) {
            setFieldError(fieldName, `${fieldName} is too large. Please limit to 5 MB.`);
            setTouched(fieldName, false);
            return;
        }

        // Goldilocks says This file is just right.
        if (file.size / 1048576 <= 100) {
            convertToBase64(file).then((result) => {
                setFieldValue(fieldName, result, true);
                setTouched(fieldName, false);
            })
            .catch((err) => console.error(err));
        }
    }, [setFieldError, setFieldValue, setTouched]);
    const fileTypes = [".doc", ".docx", ".pdf", ".rtf"];

    const onClear = useCallback((field: string) => {
        setFieldValue(field, "", true);
        setTouched(field, false);
    }, [setFieldValue, setTouched]);

    return (
        <div className={cn(styles.documents, enabled ? styles.enabled : "")}
            data-tracking-props={JSON.stringify(impression)}
            data-tracking-event-type="impression"
        >
            <p className={styles.screenHeading}>Resume*</p>
            <UploadField {...{
                name: "resume",
                id: "resume",
                label: "Upload Resume",
                fileSizeMB: 100,
                onChange,
                clear: () => onClear("resume"),
                fileTypes,
                fileName: valuesObj.resumeName,
                setFileName: (x: string) => setFieldValue("resumeName", x),
                fileStats: valuesObj.resumeStats,
                setFileStats: (x: string) => setFieldValue("resumeStats", x),
                fieldErrors: touchedObj.resume ? [errorObj?.resume].filter(Boolean) : [],
            }} />
            <p className={styles.screenHeading}>Cover Letter <span className={styles.optional}>(Optional)</span></p>
            <UploadField {...{
                name: "coverLetter",
                id: "coverLetter",
                label: "Upload Cover Letter",
                fileSizeMB: 100,
                onChange,
                clear: () => onClear("coverLetter"),
                fileTypes,
                fileName: valuesObj.coverLetterName,
                setFileName: (x: string) => setFieldValue("coverLetterName", x),
                fileStats: valuesObj.coverLetterStats,
                setFileStats: (x: string) => setFieldValue("coverLetterStats", x),
                fieldErrors: touchedObj.coverLetter ? [errorObj?.coverLetter].filter(Boolean) : [],
            }} />
        </div>
    );
});
Documents.displayName = "Documents";

export default Documents;
